import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { styled, alpha } from '@mui/material/styles';
import api from '../services/api';
import { RefreshCw, Clock, CheckCircle2, XCircle, Shuffle, BarChart3 as KpiLogoIcon, Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)'
}));

export default function PraticheKpiDashboard() {
  const { user: authUser, loading: authLoading } = useAuth();
  const roleName = (authUser?.role_name || authUser?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [legalSearch, setLegalSearch] = useState('');
  const [issuerSearch, setIssuerSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    legalId: '',
    issuerId: '',
    categoryId: ''
  });
  const [legalPage, setLegalPage] = useState(0);
  const [legalRowsPerPage, setLegalRowsPerPage] = useState(10);
  const [issuerPage, setIssuerPage] = useState(0);
  const [issuerRowsPerPage, setIssuerRowsPerPage] = useState(10);
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(10);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);

  const STATUS_ICON_CONFIG = {
    created: { Icon: Clock, color: '#0ea5e9' },
    opened: { Icon: Clock, color: '#3b82f6' },
    closed: { Icon: CheckCircle2, color: '#16a34a' },
    failed: { Icon: XCircle, color: '#b91c1c' },
    terminated: { Icon: XCircle, color: '#ef4444' },
    transition: { Icon: Shuffle, color: '#f59e0b' },
    default: { Icon: Clock, color: '#6b7280' }
  };

  const fetchKpi = async (override = {}) => {
    const enforcedLegalId = !isAdmin && authUser?.id != null ? String(authUser.id) : '';
    const next = { ...filters, ...override };
    if (!isAdmin && enforcedLegalId) {
      next.legalId = enforcedLegalId;
    }
    setLoading(true);
    try {
      const params = {};
      if (next.status) params.status = next.status;
      if (next.legalId) params.legalId = next.legalId;
      if (next.issuerId) params.issuerId = next.issuerId;
      if (next.categoryId) params.categoryId = next.categoryId;
      const res = await api.get('/reports/kpi-pratiche', { params });
      setData(res.data);
      setFilters(next);
      setLegalPage(0);
      setIssuerPage(0);
      setCategoryPage(0);
      setCustomerPage(0);
    } catch (error) {
      console.error('Error loading KPI pratiche:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;
    setTabIndex(isAdmin ? 0 : 1);
    fetchKpi({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, authUser, isAdmin]);

  useEffect(() => {
    if (!isAdmin && tabIndex === 0) {
      setTabIndex(1);
      return;
    }
    if (!filters.issuerId && tabIndex >= 2) {
      setTabIndex(1);
      return;
    }
    if (!filters.categoryId && tabIndex >= 3) {
      setTabIndex(2);
    }
  }, [filters.issuerId, filters.categoryId, isAdmin, tabIndex]);

  if (!data) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200
        }}
      >
        {loading ? (
          <RefreshCw className="animate-spin h-8 w-8 text-gray-400" />
        ) : (
          <Typography variant="h6" color="text.secondary">
            Nessun dato disponibile
          </Typography>
        )}
      </Box>
    );
  }

  const statusItems = (data.kpi?.byStatus || []).filter(
    (row) => Number(row.count || 0) > 0
  );
  const chipStyle = (color, options = {}) => ({
    borderRadius: '10px',
    bgcolor: alpha(color, options.bgAlpha ?? 0.08),
    color,
    border: `1px solid ${alpha(color, options.borderAlpha ?? 0.22)}`
  });
  const chipGreenStyle = (theme) =>
    chipStyle(theme.palette.success.dark, { bgAlpha: 0.08, borderAlpha: 0.22 });
  const legalLabel = (() => {
    if (!filters.legalId) return '';
    const match = (data.kpi?.byLegal || []).find(
      (row) => String(row.user_id) === String(filters.legalId)
    );
    return match?.user_name || `ID ${filters.legalId}`;
  })();
  const issuerLabel = (() => {
    if (!filters.issuerId) return '';
    const match = (data.kpi?.byIssuer || []).find(
      (row) => String(row.issuer_id) === String(filters.issuerId)
    );
    return match?.issuer_name || `ID ${filters.issuerId}`;
  })();
  const categoryLabel = (() => {
    if (!filters.categoryId) return '';
    const match = (data.kpi?.byCategory || []).find(
      (row) => String(row.category_id) === String(filters.categoryId)
    );
    return match?.category_name || `ID ${filters.categoryId}`;
  })();
  const hasAnyChip = Boolean(
    filters.status || filters.legalId || filters.issuerId || filters.categoryId
  );
  const canReset = Boolean(
    filters.status || (isAdmin && filters.legalId) || filters.issuerId || filters.categoryId
  );
  const legalRows = (data.kpi?.byLegal || [])
    .filter((row) => Number(row.count || 0) > 0)
    .filter((row) => {
      if (!legalSearch) return true;
      return String(row.user_name || '').toLowerCase().includes(legalSearch.toLowerCase());
    });
  const issuerRows = (data.kpi?.byIssuer || [])
    .filter((row) => Number(row.count || 0) > 0)
    .filter((row) => {
      if (!issuerSearch) return true;
      return String(row.issuer_name || '').toLowerCase().includes(issuerSearch.toLowerCase());
    });
  const categoryRows = (data.kpi?.byCategory || [])
    .filter((row) => Number(row.count || 0) > 0)
    .filter((row) => {
      if (!categorySearch) return true;
      return String(row.category_name || '').toLowerCase().includes(categorySearch.toLowerCase());
    });
  const customerRows = (data.kpi?.byCustomer || [])
    .filter((row) => Number(row.count || 0) > 0)
    .filter((row) => {
      if (!customerSearch) return true;
      return String(row.customer_name || '').toLowerCase().includes(customerSearch.toLowerCase());
    });

  return (
    <Box sx={{ p: 3 }}>
      <Box>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.default',
            pb: 2
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#eef2ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <KpiLogoIcon size={20} color="#4f46e5" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  KPI Pratiche
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Generato il {data.meta?.generatedAt || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Pratiche per stato
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                pb: 1
              }}
            >
              {statusItems.map((row) => {
                const statusValue = row.status || '';
                const isActive = filters.status === statusValue;
                const percentageLabel =
                  row.percentage != null ? row.percentage.toFixed(1) + '%' : '-';
                const key = statusValue.toLowerCase();
                const iconCfg = STATUS_ICON_CONFIG[key] || STATUS_ICON_CONFIG.default;
                const IconComp = iconCfg.Icon;
                return (
                  <Box
                    key={statusValue || 'EMPTY'}
                    sx={{ minWidth: 180, flex: '1 1 0' }}
                  >
                    <StatCard
                      onClick={() => {
                        const newStatus = isActive ? '' : statusValue;
                        setTabIndex(isAdmin ? 0 : 1);
                        fetchKpi({
                          status: newStatus,
                          legalId: '',
                          issuerId: '',
                          categoryId: ''
                        });
                      }}
                      sx={{
                        cursor: 'pointer',
                        border: isActive
                          ? '2px solid #1976d2'
                          : '1px solid rgba(145, 158, 171, 0.24)',
                        bgcolor: isActive ? 'action.selected' : 'background.paper'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 0.5
                        }}
                      >
                        <Typography variant="subtitle2">
                          {statusValue || 'Senza stato'}
                        </Typography>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '999px',
                            backgroundColor: 'rgba(148, 163, 184, 0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <IconComp size={16} color={iconCfg.color} />
                        </Box>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {row.count}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {percentageLabel} sul totale
                      </Typography>
                    </StatCard>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {hasAnyChip && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                pb: 1.5
              }}
            >
              {filters.status && (
                <Chip
                  label={`Stato: ${filters.status}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => fetchKpi({ status: '' })}
                  sx={(theme) => chipGreenStyle(theme)}
                />
              )}
              {filters.legalId && (
                <Chip
                  label={`Legale: ${legalLabel}`}
                  size="small"
                  variant="outlined"
                  onDelete={
                    isAdmin
                      ? () => {
                          setTabIndex(0);
                          fetchKpi({ legalId: '', issuerId: '', categoryId: '' });
                        }
                      : undefined
                  }
                  sx={(theme) => chipGreenStyle(theme)}
                />
              )}
              {filters.issuerId && (
                <Chip
                  label={`Controparte: ${issuerLabel}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => {
                    setIssuerSearch('');
                    setCategorySearch('');
                    setCustomerSearch('');
                    setTabIndex(1);
                    fetchKpi({ issuerId: '', categoryId: '' });
                  }}
                  sx={(theme) => chipGreenStyle(theme)}
                />
              )}
              {filters.categoryId && (
                <Chip
                  label={`Tipologia: ${categoryLabel}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => {
                    setTabIndex(2);
                    fetchKpi({ categoryId: '' });
                  }}
                  sx={(theme) => chipGreenStyle(theme)}
                />
              )}
              {canReset && (
                <Chip
                  label="Reset"
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => {
                    setTabIndex(isAdmin ? 0 : 1);
                    fetchKpi({ status: '', legalId: '', issuerId: '', categoryId: '' });
                  }}
                  sx={{ borderRadius: '10px' }}
                />
              )}
            </Box>
          )}

          <Tabs
            value={tabIndex}
            onChange={(event, nextTab) => setTabIndex(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: '1px solid rgba(145, 158, 171, 0.24)' }}
          >
            <Tab label="Referenti legali" disabled={!isAdmin} />
            <Tab label="Controparte" />
            <Tab label="Tipologia" disabled={!filters.issuerId} />
            <Tab label="Clienti" disabled={!filters.categoryId} />
          </Tabs>
        </Box>

        <Box sx={{ mt: 2 }}>
        {tabIndex === 0 && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                <TextField
                  value={legalSearch}
                  onChange={(e) => setLegalSearch(e.target.value)}
                  size="small"
                  placeholder="Cerca referente legale"
                  sx={{ width: 360, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                    endAdornment: legalSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setLegalSearch('')}>
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {legalRows.length}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Referente legale</TableCell>
                      <TableCell align="right">Pratiche</TableCell>
                      <TableCell align="right">% sul totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {legalRows
                      .slice(
                        legalPage * legalRowsPerPage,
                        legalPage * legalRowsPerPage + legalRowsPerPage
                      )
                      .map((row) => {
                        const isActive = String(filters.legalId) === String(row.user_id);
                        return (
                          <TableRow
                            key={row.user_id}
                            hover
                            selected={isActive}
                            onClick={() => {
                              if (!isAdmin) return;
                              const newLegalId = isActive ? '' : String(row.user_id);
                              setTabIndex(isActive ? 0 : 1);
                              fetchKpi({
                                legalId: newLegalId,
                                issuerId: '',
                                categoryId: ''
                              });
                            }}
                            sx={{ cursor: isAdmin ? 'pointer' : 'default' }}
                          >
                            <TableCell>{row.user_name}</TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                            <TableCell align="right">
                              {row.percentage != null ? row.percentage.toFixed(1) + '%' : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={legalRows.length}
                rowsPerPage={legalRowsPerPage}
                page={legalPage}
                onPageChange={(event, newPage) => setLegalPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setLegalRowsPerPage(parseInt(event.target.value, 10));
                  setLegalPage(0);
                }}
              />
            </Box>
          </Paper>
        )}

        {tabIndex === 1 && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                <TextField
                  value={issuerSearch}
                  onChange={(e) => setIssuerSearch(e.target.value)}
                  size="small"
                  placeholder="Cerca controparte"
                  sx={{ width: 360, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                    endAdornment: issuerSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setIssuerSearch('')}>
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {issuerRows.length}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Controparte</TableCell>
                      <TableCell align="right">Pratiche</TableCell>
                      <TableCell align="right">% sul totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {issuerRows
                      .slice(
                        issuerPage * issuerRowsPerPage,
                        issuerPage * issuerRowsPerPage + issuerRowsPerPage
                      )
                      .map((row) => {
                        const isActive = filters.issuerId === row.issuer_id;
                        return (
                          <TableRow
                            key={row.issuer_id}
                            hover
                            selected={isActive}
                            onClick={() => {
                              const newIssuerId = isActive ? '' : row.issuer_id;
                              setTabIndex(isActive ? 1 : 2);
                              fetchKpi({
                                issuerId: newIssuerId,
                                categoryId: ''
                              });
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{row.issuer_name}</TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                            <TableCell align="right">
                              {row.percentage != null ? row.percentage.toFixed(1) + '%' : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={issuerRows.length}
                rowsPerPage={issuerRowsPerPage}
                page={issuerPage}
                onPageChange={(event, newPage) => setIssuerPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setIssuerRowsPerPage(parseInt(event.target.value, 10));
                  setIssuerPage(0);
                }}
              />
            </Box>
          </Paper>
        )}

        {tabIndex === 2 && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                <TextField
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  size="small"
                  placeholder="Cerca tipologia"
                  sx={{ width: 360, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                    endAdornment: categorySearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setCategorySearch('')}>
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {categoryRows.length}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipologia</TableCell>
                      <TableCell align="right">Pratiche</TableCell>
                      <TableCell align="right">% sul totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryRows
                      .slice(
                        categoryPage * categoryRowsPerPage,
                        categoryPage * categoryRowsPerPage + categoryRowsPerPage
                      )
                      .map((row) => {
                        const isActive = filters.categoryId === row.category_id;
                        return (
                          <TableRow
                            key={row.category_id}
                            hover
                            selected={isActive}
                            onClick={() => {
                              const newCategoryId = isActive ? '' : row.category_id;
                              setTabIndex(isActive ? 2 : 3);
                              fetchKpi({
                                categoryId: newCategoryId
                              });
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{row.category_name}</TableCell>
                            <TableCell align="right">{row.count}</TableCell>
                            <TableCell align="right">
                              {row.percentage != null ? row.percentage.toFixed(1) + '%' : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={categoryRows.length}
                rowsPerPage={categoryRowsPerPage}
                page={categoryPage}
                onPageChange={(event, newPage) => setCategoryPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setCategoryRowsPerPage(parseInt(event.target.value, 10));
                  setCategoryPage(0);
                }}
              />
            </Box>
          </Paper>
        )}

        {tabIndex === 3 && (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', mt: 2 }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                <TextField
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  size="small"
                  placeholder="Cerca cliente"
                  sx={{ width: 360, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} />
                      </InputAdornment>
                    ),
                    endAdornment: customerSearch ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setCustomerSearch('')}>
                          <X size={16} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {customerRows.length}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">Pratiche</TableCell>
                      <TableCell align="right">% sul totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerRows
                      .slice(
                        customerPage * customerRowsPerPage,
                        customerPage * customerRowsPerPage + customerRowsPerPage
                      )
                      .map((row) => (
                        <TableRow key={row.customer_id}>
                          <TableCell>{row.customer_name}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell align="right">
                            {row.percentage != null ? row.percentage.toFixed(1) + '%' : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={customerRows.length}
                rowsPerPage={customerRowsPerPage}
                page={customerPage}
                onPageChange={(event, newPage) => setCustomerPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setCustomerRowsPerPage(parseInt(event.target.value, 10));
                  setCustomerPage(0);
                }}
              />
            </Box>
          </Paper>
        )}
        </Box>
      </Box>
    </Box>
  );
}
